import { Map } from '@vis.gl/react-google-maps';
import { CSSProperties, ReactNode, Children, isValidElement } from 'react';

interface MapLayoutProps {
    style?: CSSProperties;
    colorScheme?: 'LIGHT' | 'DARK' | 'FOLLOW_SYSTEM';
    gestureHandling?: 'cooperative' | 'greedy' | 'none' | 'auto';
    mapTypeId?: string;
    mapId?: string;
    zoomControl?: boolean;
    streetViewControl?: boolean;
    children?: ReactNode;
    defaultCenter?: { lat: number; lng: number };
    onMouseout?: () => void;
    onIdle?: () => void;
}

interface SlotProps {
    children?: ReactNode;
}

// Slot components - these are just markers for organizing children
const Controls: React.FC<SlotProps> = ({ children }) => <>{children}</>;
const Overlays: React.FC<SlotProps> = ({ children }) => <>{children}</>;
const Popups: React.FC<SlotProps> = ({ children }) => <>{children}</>;
const Notifications: React.FC<SlotProps> = ({ children }) => <>{children}</>;

// Helper function to extract slot content
const getSlotContent = (children: ReactNode, slotType: React.FC<SlotProps>) => {
    let slotContent: ReactNode = null;

    Children.forEach(children, (child) => {
        if (isValidElement(child) && child.type === slotType) {
            slotContent = child.props.children;
        }
    });

    return slotContent;
};

export const MapLayout: React.FC<MapLayoutProps> & {
    Controls: typeof Controls;
    Overlays: typeof Overlays;
    Popups: typeof Popups;
    Notifications: typeof Notifications;
} = ({
    style,
    colorScheme,
    gestureHandling,
    mapTypeId,
    mapId,
    zoomControl,
    streetViewControl,
    children,
    defaultCenter,
    onMouseout,
    onIdle,
}) => {
    // Extract content from slots
    const controlsContent = getSlotContent(children, Controls);
    const overlaysContent = getSlotContent(children, Overlays);
    const popupsContent = getSlotContent(children, Popups);
    const notificationsContent = getSlotContent(children, Notifications);

    // Get any children that aren't in slots (for backward compatibility)
    const otherChildren = Children.toArray(children).filter(
        (child) =>
            !isValidElement(child) ||
            (child.type !== Controls &&
             child.type !== Overlays &&
             child.type !== Popups &&
             child.type !== Notifications)
    );

    return (
        <Map
            defaultZoom={8}
            defaultCenter={defaultCenter}
            disableDefaultUI={true}
            mapTypeControl={false}
            isFractionalZoomEnabled={true}
            keyboardShortcuts={false}
            clickableIcons={false}
            streetViewControlOptions={window.google && window.google.maps && window.google.maps.ControlPosition
                ? { position: 10 }
                : undefined}
            zoomControlOptions={window.google && window.google.maps && window.google.maps.ControlPosition
                ? { position: 10 }
                : undefined}
            scaleControl={true}
            controlSize={25}
            style={style}
            colorScheme={colorScheme || 'LIGHT'}
            gestureHandling={gestureHandling}
            mapTypeId={mapTypeId || 'roadmap'}
            mapId={mapId || undefined}
            zoomControl={zoomControl}
            streetViewControl={streetViewControl}
            // onMouseout={onMouseout}
            onIdle={onIdle}
        >
            {/* Render slots in order */}
            {controlsContent}
            {overlaysContent}
            {popupsContent}
            {notificationsContent}

            {/* Backward compatibility - render non-slot children */}
            {otherChildren}
        </Map>
    );
};

// Attach slot components as static properties
MapLayout.Controls = Controls;
MapLayout.Overlays = Overlays;
MapLayout.Popups = Popups;
MapLayout.Notifications = Notifications;